import { FC, useMemo } from 'react'

import RFFFields from '../../react-final-form-fields'
import {
  validateRequired,
  validateMaxSymbols,
  composeValidators,
  validatePhone,
  validateEmail,
  getStartDateRangeValidator,
} from '../../../../util/validation'
import { nameofFactory, getPlaceholderTextFromLabel, toNumbers, capitalizeFirst, toOptions } from '../../../../util'

import { TrainingPersonFormFieldsProps, ITrainingPersonValues } from './training-person-form-types'
import { useAuthUsersSelectListQuery, useTrainingGroupsQuery } from '../../../../services'
import { useProfile } from '../../../../security'
import { useForm } from 'react-final-form'

const validateMaxSymbols100 = validateMaxSymbols(100, 'Field must not exceed 100 characters')

const nameof = nameofFactory<ITrainingPersonValues>()

const authUserLabel = 'Associated Account User'
const firstNameLabel = 'First Name'
const lastNameLabel = 'Last Name'
const emailLabel = 'Email'
const phoneLabel = 'Phone'
const startDateLabel = 'Start Date'
const trainingGroupLabel = 'Personnel Group(s)'

const TrainingPersonFormFields: FC<TrainingPersonFormFieldsProps> = props => {
  const { disabled } = props

  const form = useForm() // Get the form API

  const { airportId } = useProfile()

  const { data: trainingGroups } = useTrainingGroupsQuery({ activeOnly: true })

  const groupOptions = useMemo(
    () =>
      trainingGroups?.map(group => ({
        label: group.displayName,
        value: group.id,
      })) ?? [],
    [trainingGroups],
  )
  const authUsersQuery = useAuthUsersSelectListQuery({ airportId }, undefined)

  const authUsersOptions = useMemo(
    () => toOptions(authUsersQuery.data, { getLabel: user => capitalizeFirst(`${user.lastName}, ${user.firstName}`) }),
    [authUsersQuery.data],
  )

  const handleAuthUserChange = (selectedUserId: string | number) => {
    const selectedUser = authUsersQuery.data?.find(user => user.id === selectedUserId)
    if (selectedUser) {
      form.batch(() => {
        form.change(nameof('authUserId'), selectedUser.id)
        form.change(nameof('firstName'), selectedUser.firstName)
        form.change(nameof('lastName'), selectedUser.lastName)
        form.change(nameof('email'), selectedUser.email)
        form.change(nameof('phone'), selectedUser.phone)
      })
    } else {
      // Clear fields if no user selected
      form.batch(() => {
        form.change(nameof('authUserId'), undefined)
        form.change(nameof('firstName'), '')
        form.change(nameof('lastName'), '')
        form.change(nameof('email'), '')
        form.change(nameof('phone'), '')
      })
    }
  }

  return (
    <>
      <RFFFields.Select
        name={nameof('authUserId')}
        subLabel={'Is this personnel tied to an existing account?'}
        label={authUserLabel}
        placeholder={'Select ' + authUserLabel}
        disabled={disabled}
        options={authUsersOptions}
        onChange={handleAuthUserChange}
        isClearable
      />

      <RFFFields.Input
        name={nameof('firstName')}
        label={firstNameLabel}
        placeholder={getPlaceholderTextFromLabel(firstNameLabel)}
        disabled={disabled}
        validate={composeValidators(validateRequired, validateMaxSymbols100)}
      />

      <RFFFields.Input
        name={nameof('lastName')}
        label={lastNameLabel}
        placeholder={getPlaceholderTextFromLabel(lastNameLabel)}
        disabled={disabled}
        validate={composeValidators(validateRequired, validateMaxSymbols100)}
      />

      <RFFFields.Input
        name={nameof('email')}
        label={emailLabel}
        placeholder={'john.doe@example.com'}
        validate={validateEmail}
        disabled={disabled}
      />

      <RFFFields.MaskedInput
        name={nameof('phone')}
        label={phoneLabel}
        placeholder="___-___-____"
        parse={toNumbers as (value: string) => string}
        validate={validatePhone}
        type="phone"
        disabled={disabled}
      />

      <RFFFields.DatePicker
        name={nameof('hireDate')}
        label={startDateLabel}
        info="Used as the default start date for training requirements."
        placeholderText={getPlaceholderTextFromLabel(startDateLabel)}
        disabled={disabled}
        validate={getStartDateRangeValidator('endDate')}
        isDateOnly
        shouldCloseOnSelect
        showMonthDropdown
        showYearDropdown
        popperPlacement="top-end"
      />

      <RFFFields.Select
        name={nameof('trainingGroupIds')}
        label={trainingGroupLabel}
        placeholder={'Select ' + trainingGroupLabel}
        disabled={disabled}
        options={groupOptions}
        isMulti
      />
    </>
  )
}

export default TrainingPersonFormFields
